import React, { useState } from "react";
import { Row, Col, Form, Input, Card, Button, Tooltip } from "antd";
import "./styles.css";
import { useSelector, useDispatch } from "react-redux";
import { PlusCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { numericInputValidator } from "../../../Common/Utils";
import {
  setCurrentStep,
  setRideDetails,
} from "../../../../reducers/testRideSlice";
import { TEST_RIDE, UNITS } from "../../../Common/Constants";
import TimeRangeForm from "./TimeRangeDetails";

const BeforeRideForm = ({timeRangeValidation}) => {
  const dispatch = useDispatch();
  const beforeRideFormRef = React.useRef(null);
  const rideDetails = useSelector((state) => state.testRide?.rideDetails);
  const [observationList, setObservationList] = useState(
    rideDetails?.observationParameters || [""]
  );

  const onBack = () => {
    const { climate, level, ...inputValues } =
      beforeRideFormRef.current?.getFieldValue();

    dispatch(
      setRideDetails({
        ...rideDetails,
        ...inputValues,
        observationParameters: observationList,
      })
    );
    dispatch(setCurrentStep(0));
  };

  const onFinish = (value) => {
    const { startDate, startTime, endDate, endTime } = value;
    const errorList = timeRangeValidation(startDate, startTime, endDate, endTime)
    if(errorList){
      beforeRideFormRef?.current?.setFields(errorList);
    } else {
      dispatch(
        setRideDetails({
          ...rideDetails,
          ...value,
          observationParameters: observationList,
        })
      );
      dispatch(setCurrentStep(2));
    }
  };

  const observationChange = (index, value) => {
    let observation = [...observationList];
    observation[index] = value;
    setObservationList(observation);
  };

  const addObservationList = () => {
    setObservationList((prev) => [...prev, ""]);
  };

  const removeObservationList = (index) => {
    let observation = [...observationList];
    observation.splice(index, 1);
    setObservationList(observation);
  };

  return (
    <Form
      name="beforRideForm"
      layout="vertical"
      ref={beforeRideFormRef}
      onFinish={onFinish}
      initialValues={{
        ...rideDetails,
        observationParameters: rideDetails?.observationParameters || [],
      }}
      validateMessages={{
        required: TEST_RIDE.FORM.REQUIRED_FIELDS.BLANK_ERROR,
        whitespace: TEST_RIDE.FORM.REQUIRED_FIELDS.BLANK_ERROR,
      }}
      autoComplete="off"
    >
      <Row gutter={16}>
        <TimeRangeForm formRef={beforeRideFormRef} />
        <Col xs={24} sm={24} md={24} lg={16} xl={16} xxl={18}>
          <Card bordered={false} className="input-box">
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="rideStartPlaceName"
                  label={TEST_RIDE.FORM.REQUIRED_FIELDS.STARTING_LOCATION}
                >
                  <Input
                    title="rideStartPlaceName"
                    placeholder="Starting Location"
                    maxLength={50}
                    size="large"
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="goal"
                  label={TEST_RIDE.FORM.REQUIRED_FIELDS.GOAL}
                >
                  <Input.TextArea
                    size="large"
                    showCount
                    className="input-count-show-up"
                    maxLength={350}
                    rows={6}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name={"observationParameters"}
                  label={TEST_RIDE.FORM.REQUIRED_FIELDS.OBSERVATION_PARAMETERS}
                >
                  <div style={{ maxHeight: "160px", overflowY: "scroll" }}>
                    {observationList.map((observation, index) => (
                      <Row key={index} justify={"center"} align="top">
                        <Col flex="auto">
                          <Input
                            title="Observation"
                            placeholder="Observation"
                            className="mb1"
                            maxLength={50}
                            value={observation}
                            onChange={(event) =>
                              observationChange(index, event.target.value)
                            }
                            size="large"
                            allowClear
                          />
                        </Col>
                        <Col flex="30px" className="mt05">
                          {index + 1 === observationList?.length ? (
                            <Tooltip title="Add">
                              <PlusCircleOutlined
                                className="input-add-icon"
                                onClick={addObservationList}
                              />
                            </Tooltip>
                          ) : (
                            <Tooltip title="Remove">
                              <CloseCircleOutlined
                                className="input-close-icon"
                                onClick={() => removeObservationList(index)}
                              />
                            </Tooltip>
                          )}
                        </Col>
                      </Row>
                    ))}
                  </div>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={12} sm={6}>
                <Form.Item
                  name="startAmbient"
                  label={<span>{TEST_RIDE.FORM.REQUIRED_FIELDS.AMBIENT} <span className="f12 grey">({UNITS.CELSIUS})</span></span>}
                  rules={[
                    {
                      message: "Enter a valid number",
                      validator: (_, value) => numericInputValidator(value, "decimalNegative"),
                    },
                  ]}
                >
                  <Input
                    size="large"
                    maxLength={6}
                    placeholder="Ambient Temperatures"
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col xs={12} sm={6}>
                <Form.Item
                  name="startMCU"
                  label={<span>{TEST_RIDE.FORM.REQUIRED_FIELDS.MCU} <span className="f12 grey">({UNITS.CELSIUS})</span></span>}
                  rules={[
                    {
                      message: "Enter a valid number",
                      validator: (_, value) => numericInputValidator(value, "decimalNegative"),
                    },
                  ]}
                >
                  <Input
                    size="large"
                    maxLength={6}
                    placeholder="MCU Temperatures"
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col xs={12} sm={6}>
                <Form.Item
                  name="startMotor"
                  label={<span>{TEST_RIDE.FORM.REQUIRED_FIELDS.MOTOR} <span className="f12 grey">({UNITS.CELSIUS})</span></span>}
                  rules={[
                    {
                      message: "Enter a valid number",
                      validator: (_, value) => numericInputValidator(value, "decimalNegative"),
                    },
                  ]}
                >
                  <Input
                    size="large"
                    maxLength={6}
                    placeholder="Motor Temperatures"
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col xs={12} sm={6}>
                <Form.Item
                  name="startBattery"
                  label={<span>{TEST_RIDE.FORM.REQUIRED_FIELDS.BATTERY} <span className="f12 grey">({UNITS.CELSIUS})</span></span>}
                  rules={[
                    {
                      message: "Enter a valid number",
                      validator: (_, value) => numericInputValidator(value, "decimalNegative"),
                    },
                  ]}
                >
                  <Input
                    size="large"
                    maxLength={6}
                    placeholder="Battery Temperatures"
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="startSOC"
                  className="mb0"
                  label={<span>{TEST_RIDE.FORM.REQUIRED_FIELDS.SOC} <span className="f12 grey">({UNITS.PERCENT})</span></span>}
                  rules={[
                    {
                      message: "Enter a valid number",
                      validator: (_, value) => numericInputValidator(value, "decimalNegative"),
                    },
                  ]}
                >
                  <Input
                    size="large"
                    maxLength={6}
                    placeholder="Battery Percentage"
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  className="mb0"
                  name="startVoltage"
                  label={<span>{TEST_RIDE.FORM.REQUIRED_FIELDS.VOLTAGE} <span className="f12 grey">({UNITS.VOLTS})</span></span>}
                  rules={[
                    {
                      message: "Enter a valid number",
                      validator: (_, value) => numericInputValidator(value, "decimalNegative"),
                    },
                  ]}
                >
                  <Input
                    size="large"
                    maxLength={6}
                    placeholder="Battery Voltage"
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Col span={24}>
        <Form.Item
          className="mt2"
          style={{ display: "flex", justifyContent: "flex-end" }}
        >
          <Row>
            <Button
              role="BackBtn"
              className="back-btn"
              htmlType="button"
              style={{ width: "80px" }}
              onClick={onBack}
            >
              Back
            </Button>
            <Button
              data-testid="submitBtn"
              type="primary"
              className="submit-btn"
              htmlType="submit"
              style={{ width: "80px", marginLeft: "2rem" }}
            >
              Next
            </Button>
          </Row>
        </Form.Item>
      </Col>
    </Form>
  );
};

export default BeforeRideForm;
